/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { bindTrigger, bindMenu } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Tooltip } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import logger from "services/logger";
import { toLocalIsoDate } from "utils/date";
import { Viewer } from "components/auth/types";
import { useSnackbar } from "components/haven-design/snackbar";
import { FormType, sendFormToInterests, sendFormToInterestsVariables, skipInterestsAction, skipInterestsActionVariables, addNoteToInterests, addNoteToInterestsVariables } from "types/gql-types";
import ReasonModal, { Action, BulkAction } from "pages/dashboard/interests-profile/components/reason-modal";
import { SKIP_INTERESTS, SEND_FULL_APP_TO_INTERESTS, ADD_NOTE_TO_INTERESTS } from "pages/dashboard/interests-profile/components/actions-query";
import SendFullAppModal from "../send-full-app-modal";
import { TableResult, useInterestsTable } from "../interests-table/table-context";
import { useCanAssignOwner } from "../interests-table/can-assign-owner";
import { useSidebarFilters } from "../sidebar-filters";
import { MessageButton } from "./button-message";

import CsvButton from "./button-csv";
import ButtonChangeStatus from "./button-change-status";
import SkipButton from "./button-action-skip";
import AddNoteButton from "./button-action-add-note";
import OwnerActionButton from "./button-action-owner";
import AssignOwnerModal from "./assign-owner-modal";
import RemoveOwnerModal from "./remove-owner-modal";
import { BulkOwnerResult } from "./bulk-owner-feedback";
import { getScope, useBulkElasticQuery } from "./bulk-scope";
import { countOf } from "./utils";


type ActionButtonType = {
  sendFullAppToInterestsEnabled: boolean;
  selectedNames: TableResult[];
  interestListId: string;
  viewer: Viewer;
  setMessageModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCSVModal: (value: boolean) => void;
  onStatusChange: (status: string, reason: string | null) => Promise<any>;
  statusChangeLoading: boolean;
  defaultSorting?: string;
};

// eslint-disable-next-line max-statements
const ActionButton: React.FC<ActionButtonType> = ({
  sendFullAppToInterestsEnabled,
  selectedNames,
  interestListId,
  viewer,
  setMessageModalOpen,
  onStatusChange,
  setShowCSVModal,
  statusChangeLoading,
  defaultSorting,
}: ActionButtonType) => {
  const { openSnackbar } = useSnackbar();
  const {
    selectedInterestIds,
    hasSelectedAllInterests,
    selectedPageRange,
    totalSelectedInterests,
    deselectAllInterests,
    assignOwnerModalOpen,
    setAssignOwnerModalOpen,
  } = useInterestsTable();
  const { search, interestListIds } = useSidebarFilters();

  // M3-T02 — UI-only "who can assign" gate. When false the Assign/Remove Owner
  // menu items are hidden (absent from the DOM), not disabled.
  const canAssignOwner = useCanAssignOwner(viewer);

  const [sendFullAppToInterests] =
    useMutation<sendFormToInterests, sendFormToInterestsVariables>(
      SEND_FULL_APP_TO_INTERESTS,
    );
  const [skipInterests, { loading: isSkippingInterests }] =
  useMutation<skipInterestsAction, skipInterestsActionVariables>(SKIP_INTERESTS);
  const [addNoteToInterestsMutation, { loading: isAddingNote }] =
  useMutation<addNoteToInterests, addNoteToInterestsVariables>(ADD_NOTE_TO_INTERESTS);
  const [isSendFullAppOpen, setIsSendFullAppOpen] = React.useState(false);
  const [reason, setReason] = useState("");
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [removeOwnerModalOpen, setRemoveOwnerModalOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const closeReasonModal = () => setSkipModalOpen(false);
  const closeAddNoteModal = () => setAddNoteModalOpen(false);
  const closeAssignOwnerModal = () => setAssignOwnerModalOpen(false);
  const closeRemoveOwnerModal = () => setRemoveOwnerModalOpen(false);
  // The flag lives in the table provider, which outlives this toolbar across navigation.
  React.useEffect(() => () => setAssignOwnerModalOpen(false), [setAssignOwnerModalOpen]);
  let waitForIndex = false;

  const popupState = usePopupState({ variant: "popover", popupId: "interests-toolbar-actions-menu" });

  // Carries the table's active sort so page-range scopes hit the rows on
  // screen (HAV-162) — see bulk-scope.ts.
  const elasticQuery = useBulkElasticQuery(defaultSorting);

  const withQuery = hasSelectedAllInterests || (
    selectedPageRange.from !== null || selectedPageRange.to !== null
  );

  // Shared bulk scope for the owner modals — same shape as the add-note action.
  const ownerBulkScope = {
    interestListIds: [interestListId],
    pageFrom: selectedPageRange.from,
    pageTo: selectedPageRange.to,
    ...getScope(withQuery, elasticQuery, selectedInterestIds),
  };


  const onSendFullApp = (deadline: Date) => {
    const variables = {
      interestIds: selectedInterestIds,
      type: FormType.FULL_APPLICATION,
      deadline: toLocalIsoDate(deadline),
    };

    sendFullAppToInterests({ variables })
      .then(() => {
        openSnackbar({ message: "Full application request sent.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "There was a problem sending full application request.", status: "error" });
      })
      .finally(() => {
        setIsSendFullAppOpen(false);
      });
  };

  const onSkipInterests = () => {
    const variables = {
      interestListIds: [interestListId],
      reason,
      pageFrom: selectedPageRange.from,
      pageTo: selectedPageRange.to,
      ...getScope(withQuery, elasticQuery, selectedInterestIds),
    };

    if (!reason) {
      openSnackbar({ message: "Reason can't be empty", status: "error", duration: 5000 });
      return;
    }
    skipInterests({ variables })
      .then(() => {
        waitForIndex = true;
        openSnackbar({ message: `${totalSelectedInterests} applicants skipped.`, status: "success", duration: 5000 });

        setTimeout(() => {
          search();
          waitForIndex = false;
        // extra delay for index propagation
        }, 2000);
      })
      .catch((err) => {
        const errMsg = `
          Error skipping applicants. Please refresh before retrying.
          If the problem persists please contact support@havenconnect.com`;
        openSnackbar({ message: errMsg, status: "error", duration: 5000 });
        logger.sendError(err);
      })
      .finally(closeReasonModal);
  };

  const onAddNote = () => {
    const variables = {
      interestListIds: [interestListId],
      note: noteText,
      pageFrom: selectedPageRange.from,
      pageTo: selectedPageRange.to,
      ...getScope(withQuery, elasticQuery, selectedInterestIds),
    };

    if (!noteText || noteText.length < 1) {
      openSnackbar({ message: "Note can't be empty", status: "error", duration: 5000 });
      return;
    }
    if (noteText.length > 5000) {
      openSnackbar({ message: "Note must be less than 5000 characters", status: "error", duration: 5000 });
      return;
    }
    addNoteToInterestsMutation({ variables })
      .then(() => {
        openSnackbar({ message: `Note added to ${totalSelectedInterests} interests.`, status: "success", duration: 5000 });
        setNoteText("");
      })
      .catch((err) => {
        const errMsg = `
          Error adding note. Please refresh before retrying.
          If the problem persists please contact support@havenconnect.com`;
        openSnackbar({ message: errMsg, status: "error", duration: 5000 });
        logger.sendError(err);
      })
      .finally(closeAddNoteModal);
  };

  // M2-T11 — success vs partial is decided here, not in the modals: the happy path
  // runs only when every interest succeeded. A partial failure leaves the toast,
  // refetch, and selection untouched so it falls through to M2-T12.
  const isAllSuccess = (result: BulkOwnerResult): boolean =>
    result.failed.length === 0 && result.succeeded.length > 0;

  const onOwnerSuccess = (verb: "assigned to" | "removed from") => (result: BulkOwnerResult) => {
    if (!isAllSuccess(result)) return;
    openSnackbar({
      message: `Owner ${verb} ${countOf(result.succeeded.length, "applicant")}.`,
      status: "success",
      duration: 5000,
    });
    deselectAllInterests();
    // The owner write reindexes and refreshes Elasticsearch before responding.
    search();
  };

  const onAssignOwnerSuccess = onOwnerSuccess("assigned to");
  const onRemoveOwnerSuccess = onOwnerSuccess("removed from");

  const hasSelectedInterests = totalSelectedInterests > 0;

  const button = <>
    <Button id="interests-toolbar-actions" color="primary" {...bindTrigger(popupState)} disabled={!hasSelectedInterests} style={{ lineHeight: "2", minHeight: "100%" }}>
      Bulk Actions {totalSelectedInterests > 0 && `(${totalSelectedInterests})`}
      <span className="caret" style={{ marginLeft: 4 }} />
    </Button>
    <Menu {...bindMenu(popupState)}>
      {sendFullAppToInterestsEnabled && interestListIds.length === 1 && (
        <MenuItem
          disabled={selectedInterestIds.length === 0}
          id="send-full-application"
          onMouseUp={() => {
            setIsSendFullAppOpen(true);
            popupState.close();
          }}
        >Send Full Application {`(${selectedInterestIds.length})`}
        </MenuItem>
      )}
      {interestListIds.length === 1 && (
        <MessageButton
          setMessageModalOpen={setMessageModalOpen}
          viewer={viewer}
          recipients={selectedNames}
          currentListId={interestListId}
          actionsButton
          closeMenu={popupState.close}
        />)}
      <ButtonChangeStatus
        onStatusChange={onStatusChange}
        isAvailable={hasSelectedInterests}
        actionsButton
        closeMenu={popupState.close}
        loading={statusChangeLoading}
      />
      {interestListIds.length === 1 && (
        <SkipButton
          selectedInterestIds={selectedInterestIds.slice()}
          closeMenu={popupState.close}
          setSkipModalOpen={setSkipModalOpen}
        />)}
      {interestListIds.length === 1 && (
        <AddNoteButton
          selectedInterestIds={selectedInterestIds.slice()}
          closeMenu={popupState.close}
          setAddNoteModalOpen={setAddNoteModalOpen}
        />)}
      {canAssignOwner && interestListIds.length === 1 && (
        <OwnerActionButton
          action="assign"
          closeMenu={popupState.close}
          setModalOpen={setAssignOwnerModalOpen}
        />)}
      {canAssignOwner && interestListIds.length === 1 && (
        <OwnerActionButton
          action="remove"
          closeMenu={popupState.close}
          setModalOpen={setRemoveOwnerModalOpen}
        />)}
      <CsvButton
        viewer={viewer}
        actionsButton
        closeMenu={popupState.close}
        setShowCSVModal={setShowCSVModal}
      />
    </Menu>
    <SendFullAppModal
      interestCount={selectedInterestIds.length}
      onSubmit={(deadline: Date) => onSendFullApp(deadline)}
      onRequestClose={() => setIsSendFullAppOpen(false)}
      onCancel={() => setIsSendFullAppOpen(false)}
      isOpen={isSendFullAppOpen}
    />
    <ReasonModal
      isOpen={skipModalOpen}
      newStatus={Action.Skip}
      currentStatus={Action.OnWaitlist}
      onSubmit={() => onSkipInterests()}
      onChange={(input) => setReason(input)}
      reason={reason}
      onRequestClose={closeReasonModal}
      onCancel={closeReasonModal}
      isSubmitting={isSkippingInterests || waitForIndex}
      bulkAction
    />
    <ReasonModal
      isOpen={addNoteModalOpen}
      newStatus={BulkAction.AddNote}
      currentStatus={Action.OnWaitlist}
      onSubmit={() => onAddNote()}
      onChange={(input) => setNoteText(input)}
      reason={noteText}
      onRequestClose={closeAddNoteModal}
      onCancel={closeAddNoteModal}
      isSubmitting={isAddingNote}
      bulkAction
      selectedCount={totalSelectedInterests}
    />
    <AssignOwnerModal
      isOpen={assignOwnerModalOpen}
      onRequestClose={closeAssignOwnerModal}
      onCancel={closeAssignOwnerModal}
      selectedCount={totalSelectedInterests}
      currentUserId={viewer.id}
      currentUserName={viewer.fullName ?? ""}
      // Adapt the assign modal's succeeded/failed id lists to BulkOwnerResult so
      // the all-success gate matches the remove flow.
      onResult={(succeeded, failed) => onAssignOwnerSuccess({
        succeeded,
        failed: failed.map((interestId) => ({ interestId, name: null, reason: null })),
      })}
      {...ownerBulkScope}
    />
    <RemoveOwnerModal
      isOpen={removeOwnerModalOpen}
      onRequestClose={closeRemoveOwnerModal}
      onCancel={closeRemoveOwnerModal}
      selectedCount={totalSelectedInterests}
      onSuccess={onRemoveOwnerSuccess}
      {...ownerBulkScope}
    />
  </>;
  if (totalSelectedInterests === 50000) {
    return <Tooltip title="Search is too large to perform bulk actions">
      <div style={{ height: "100%" }}>Disabled</div>
    </Tooltip>;
  }

  return hasSelectedInterests ? button : <Tooltip title="Select Applications First">
    <div style={{ height: "100%" }}>{button}</div>
  </Tooltip>;
};

export default ActionButton;
